import httpClient from "@/config/axios";
import type { DailyShiftReplaceRequest } from "@/pages/roster-create/service/getDailyShift";
import type { RosterCreateConfigPayload } from "@/pages/roster-create/service/rosterCreateConfigPayload";

export type PublishRosterRequest = {
  readonly groupId: string | null | undefined;
  readonly scheduleId: string;
  readonly issueComment?: string;
};

export type UnpublishRosterRequest = {
  readonly scheduleId: string;
};

export type SaveRosterConfigRequest = {
  readonly config: Partial<RosterCreateConfigPayload>;
  readonly groupId?: string;
};

export type DeleteRosterConfigPresetRequest = {
  readonly configId: number;
  readonly groupId?: string;
};

export type CreateRosterWithWeeklyOffRequest = {
  readonly year: number;
  readonly month: number;
  readonly groupId?: string;
};

export type RosterCreateCommandResponse = {
  readonly message?: string;
  readonly schedule_id?: string;
  readonly scheduleId?: string;
};

export type ResignationPartialResolveRequest = {
  readonly scheduleId: string;
  readonly resignedNurseId: string;
  readonly cutoffDate: string;
  readonly replacementPreceptorId?: string | null;
};

export type ResignationPartialResolveChangeKind =
  | "resigned"
  | "off_to_work"
  | "work_to_off"
  | "shift_change";

export type ResignationPartialResolveResponse = {
  readonly schedule_id: string;
  readonly base_schedule_id: string;
  readonly cutoff_date: string;
  readonly resigned_nurse: {
    readonly nurse_id: string;
    readonly name?: string | null;
  };
  readonly summary?: {
    readonly nurses_touched?: number;
    readonly cells_changed?: number;
    readonly warnings?: readonly string[];
  };
  readonly changed_nurses?: readonly {
    readonly nurse_id: string;
    readonly name?: string | null;
    readonly changes?: readonly {
      readonly date: string;
      readonly kind: ResignationPartialResolveChangeKind;
      readonly before?: string | null;
      readonly after?: string | null;
    }[];
  }[];
  readonly roster?: unknown;
};

export type DeleteRosterConfigPresetResponse = {
  readonly message?: string;
  readonly config_id?: number;
};

export async function publishRosterApi({
  groupId,
  scheduleId,
  issueComment = "",
}: PublishRosterRequest): Promise<boolean> {
  await httpClient.post(
    `/roster/publish?group_id=${encodeURIComponent(groupId ?? "")}`,
    {
      schedule_id: scheduleId,
      issue_comment: issueComment,
    },
    {
      actionTrack: {
        event: "roster_create.roster.publish_toggle",
        section: "roster_create",
        props: {
          action: "publish",
          schedule_id: scheduleId,
          group_id: groupId ?? undefined,
        },
      },
    }
  );
  return true;
}

export async function unpublishRosterApi({
  scheduleId,
}: UnpublishRosterRequest): Promise<void> {
  await httpClient.post("/roster/unpublish", null, {
    params: { schedule_id: scheduleId },
    actionTrack: {
      event: "roster_create.roster.publish_toggle",
      section: "roster_create",
      props: {
        action: "unpublish",
        schedule_id: scheduleId,
      },
    },
  });
}

export async function saveRosterConfigApi({
  config,
  groupId,
}: SaveRosterConfigRequest): Promise<RosterCreateCommandResponse> {
  return httpClient.post<RosterCreateCommandResponse>(
    "/roster/config/save",
    config,
    {
      params: groupId ? { group_id: groupId } : undefined,
      actionTrack: {
        event: "roster_create.roster.generate_save",
        section: "roster_create",
        props: {
          config_id: config.config_id,
          group_id: groupId,
        },
      },
    }
  );
}

export async function deleteRosterConfigPresetApi({
  configId,
  groupId,
}: DeleteRosterConfigPresetRequest): Promise<DeleteRosterConfigPresetResponse> {
  return httpClient.delete<DeleteRosterConfigPresetResponse>(
    `/roster/config/${configId}`,
    {
      params: groupId ? { group_id: groupId } : undefined,
      actionTrack: {
        event: "roster_create.config.preset_delete",
        section: "roster_create",
        props: {
          config_id: configId,
          group_id: groupId,
        },
      },
    }
  );
}

export async function createRosterWithWeeklyOffApi({
  year,
  month,
  groupId,
}: CreateRosterWithWeeklyOffRequest): Promise<RosterCreateCommandResponse> {
  const params = new URLSearchParams({
    year: String(year),
    month: String(month),
  });

  if (groupId) {
    params.append("group_id", groupId);
  }

  return httpClient.post<RosterCreateCommandResponse>(
    `/roster/create-with-weekly-off?${params.toString()}`,
    undefined,
    {
      actionTrack: {
        event: "roster_create.roster.create_empty",
        section: "roster_create",
        props: {
          year,
          month,
          group_id: groupId,
        },
      },
    }
  );
}

export async function copyRosterApi(
  scheduleId: string
): Promise<RosterCreateCommandResponse> {
  return httpClient.post<RosterCreateCommandResponse>(
    `/roster/copy/${scheduleId}`,
    undefined,
    {
      actionTrack: {
        event: "roster_create.roster.copy",
        section: "roster_create",
        props: {
          schedule_id: scheduleId,
        },
      },
    }
  );
}

export async function resolveResignationPartialApi({
  scheduleId,
  resignedNurseId,
  cutoffDate,
  replacementPreceptorId = null,
}: ResignationPartialResolveRequest): Promise<ResignationPartialResolveResponse> {
  return httpClient.post<ResignationPartialResolveResponse>(
    "/roster_create/partial-resolve/resignation",
    {
      schedule_id: scheduleId,
      resigned_nurse_id: resignedNurseId,
      cutoff_date: cutoffDate,
      replacement_preceptor_id: replacementPreceptorId,
    },
    { timeout: 60000 }
  );
}

export async function replaceDailyShiftApi(
  payload: DailyShiftReplaceRequest
): Promise<void> {
  await httpClient.put("/daily-shift", payload);
}
